/** Make internal links carry the console-wide tenant scope into NEW tabs.
 *
 * `TenantScopeUrlSync` keeps the *current* tab's address bar correct, but it
 * cannot change what a link's `href` attribute SAYS — so ⌘-click, middle-click
 * and "Copy link address" would all hand out an unscoped URL. Rather than
 * rewriting the 91 files that import `Link` (and still missing hand-written
 * `<a href>` and `window.open`), one delegated listener patches the anchor the
 * pointer is actually on, just before the browser reads it:
 *
 *     pointerdown │ auxclick │ contextmenu   (capture, on document)
 *         ↓  target.closest('a[href]')
 *         ↓  internal, not a download
 *     href = withScope(href, scope ids)
 *
 * Plain left-clicks are unaffected in behaviour: React Router's own handler
 * navigates from `to`, not from the patched attribute, and the URL sync
 * re-stamps the scope on arrival either way.
 *
 * Yes, this is a DOM-level trick. It is contained to this file, it is the only
 * place in the app that mutates someone else's `href`, and it is unit-tested.
 *
 * Patched anchors are marked `data-cp-scope` with the ids written, so that a
 * later scope change re-patches them (a React-rendered `<Link>` may not
 * re-render between two scope changes) while a link whose own `to` carries an
 * explicit `?scope=` — which must always win — is left alone.
 */
import { useEffect } from 'react';

import { SCOPE_PARAM, isInternalHref, withScope } from './scopeUrl';
import { formatTenantIds } from './tenantIds';
import { getTenantScopeList } from './tenantScope';

const MARK = 'data-cp-scope';
const EVENTS = ['pointerdown', 'auxclick', 'contextmenu'] as const;

/** Apply the current scope (its operator ids; one id or none also work) to one
 *  anchor, or undo a previous patch when the scope was cleared. Returns silently
 *  for anything it should not touch. */
export function patchAnchor(
  a: HTMLAnchorElement,
  scope: number | readonly number[] | null,
  origin: string,
): void {
  if (a.hasAttribute('download')) return;
  const href = a.getAttribute('href');
  if (href === null || !isInternalHref(href, origin)) return;

  const scopeKey = formatTenantIds(scope === null ? [] : typeof scope === 'number' ? [scope] : scope);
  const marked = a.getAttribute(MARK);
  if (marked !== null && marked !== scopeKey) {
    // Our own stale patch — drop it so the new scope (or no scope) applies.
    const u = new URL(href, origin);
    u.searchParams.delete(SCOPE_PARAM);
    const bare = `${u.pathname}${u.search}${u.hash}`;
    a.setAttribute('href', bare);
    a.removeAttribute(MARK);
    if (scopeKey === null) return;
    patchAnchor(a, scope, origin);
    return;
  }
  if (scopeKey === null || marked !== null) return;

  const next = withScope(href, scope, origin);
  if (next === href) return; // already carries an explicit scope — it wins
  a.setAttribute('href', next);
  a.setAttribute(MARK, scopeKey);
}

export function useScopeAwareAnchors(): void {
  useEffect(() => {
    // The scope is read at event time, so the listener never needs re-attaching
    // when it changes — the handler closes over nothing but the store itself.
    const handler = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      patchAnchor(
        anchor,
        getTenantScopeList().map((s) => s.id),
        window.location.origin,
      );
    };
    for (const type of EVENTS) document.addEventListener(type, handler, true);
    return () => {
      for (const type of EVENTS) document.removeEventListener(type, handler, true);
    };
  }, []);
}
